import { Grid, useTheme, useMediaQuery, Box, Avatar, Card, Tooltip, ButtonBase, CardContent, Typography } from "@mui/material"
import { useLocaleState, useTranslate } from 'react-admin'
import DeviceSubscriptionsSummary from "./DeviceSubscriptionsSummary";
import NumberOfDevicesInAccount from "./NumberOfDevicesInAccount";
import SmsCreditSummary from "./SmsCreditSummary";
import LocationsSummary from "./LocationsSummary";
import NumberOfNonAcknowledgeAlerts from "./NumberOfNonAcknowledgeAlerts";
import { useEffect, useState } from "react";
import { getDevicesSummary, getUserUiSetup } from "../../rest/restClient";
import numberOfDevicesByProducts from "./NumberOfDevicesByProducts";
import NumberOfAlerts from "./NumberOfAlerts";
import NumberOfStationaryDevices from "./NumberOfStationaryDevices";
import NumberOfDevicesOutsideOrganizationBoundary from "./NumberOfDevicesOutsideOrganizationBoundary";
import BroadcastMessages from "./BroadcastMessages";
import { USER_UI_SETUP_ACTIVE_DASHBOARD_WIDGETS } from "../configuration/actions";
import { getUserId, getValidAccessToken } from "../../auth/tokenStore";

// Essential default widgets when getUserUiSetup fails
const DEFAULT_FALLBACK_WIDGETS = [
    "locations_summary",
    "number_of_devices_in_account", 
    "sms_credit_summary"
];

const Dashboard = ({ }) => {
    const [userId, setUserId] = useState(getUserId);
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const translate = useTranslate();
    const [locale, setLocale] = useLocaleState();
    const [devicesSummary, setDevicesSummary] = useState(null);
    const [activeDashboardWidgets, setActiveDashboardWidgets] = useState(null);
    const [isWidgetsLoaded, setIsWidgetsLoaded] = useState(false);

    // The user-info cache can be empty while the session is valid (fresh
    // tab, cache cleared): hydrate it from the token instead of bouncing to
    // /init-login — which, with a live session, would only bounce back here.
    // No session at all is react-admin's job (auth check → logout).
    useEffect(() => {
        if (userId) return undefined;
        let active = true;
        getValidAccessToken()
            .then(() => { if (active) setUserId(getUserId()); })
            .catch(() => { });
        return () => { active = false; };
    }, [userId]);

    useEffect(async () => {
        if (!userId) return;
        getUserUiSetup(userId).then((res) => {
            let activeWidgets;
            if (res && res.status === 200 && res.json && res.json.active_dashboard_widgets) {
                localStorage.setItem(USER_UI_SETUP_ACTIVE_DASHBOARD_WIDGETS, res.json.active_dashboard_widgets)
                activeWidgets = res.json.active_dashboard_widgets;
            } else {
                activeWidgets = DEFAULT_FALLBACK_WIDGETS;
            }
            
            setActiveDashboardWidgets(activeWidgets);
            setIsWidgetsLoaded(true); // Allow widgets to render immediately
            
            // Only load devicesSummary if widgets that need it are active
            const widgetsThatNeedDevicesSummary = [
                "number_of_devices_in_account",
                "number_of_devices_in_account_by_products"
            ];
            
            const needsDevicesSummary = activeWidgets && activeWidgets.some(widget => 
                widgetsThatNeedDevicesSummary.includes(widget)
            );
            
            if (needsDevicesSummary) {
                const needsProductsData = activeWidgets.includes("number_of_devices_in_account_by_products");
                
                // Run this in parallel - don't wait for it to complete
                getDevicesSummary(needsProductsData).then((res) => {
                    if (res && res.status === 200) {
                        setDevicesSummary(res.json)
                    } else {
                        console.warn('DevicesSummary API returned non-200 status:', res?.status);
                        setDevicesSummary({}) // Set empty object on error
                    }
                }).catch((error) => {
                    console.error('Failed to load devicesSummary:', error);
                    setDevicesSummary({}) // Set empty object on network error
                });
            } else {
                // Set empty object immediately if not needed
                setDevicesSummary({});
            }
        })
    }, [userId])

    // Only wait for widgets config to load, not devicesSummary (which can load in parallel)
    if (!userId || !activeDashboardWidgets || !isWidgetsLoaded) return null

    return (
        <>
            <Typography sx={{ color: theme.palette.text.primary, textAlign: 'start', fontWeight: 700, fontSize: "24px", lineHeight: "42px", margin: "0px 0px 16px 0px" }}>
                {translate('resources.dashboard.name')}
            </Typography>
            <Grid
                container
                spacing={isSmall ? 0 : 1}
                sx={{
                    margin: '0px',
                    width: "100%",
                    backgroundColor: theme.palette.background.default,
                }}
            >


                <BroadcastMessages />

                <Grid
                    item
                    sm={12}
                    md={6}
                    sx={{
                        width: "100%",
                        margin: isSmall ? "8px 0px 0px 0px" : '0px',
                    }} >
                    <Grid
                        container
                        spacing={isSmall ? 0 : 1}
                        sx={{
                            margin: '0px',
                            width: "100%",
                            backgroundColor: theme.palette.background.default,
                        }}
                    >
                        {
                            activeDashboardWidgets.includes("number_of_devices_in_account") &&
                            <NumberOfDevicesInAccount devicesSummary={devicesSummary} />

                        }
                        {
                            activeDashboardWidgets.includes("number_of_alerts") &&
                            <NumberOfAlerts />

                        }
                        {
                            activeDashboardWidgets.includes("sms_credit_summary") &&
                            <SmsCreditSummary />

                        }
                        {
                            activeDashboardWidgets.includes("device_subscriptions_summary") &&
                            <DeviceSubscriptionsSummary />

                        }
                        {
                            activeDashboardWidgets.includes("number_of_stationary_devices") &&
                            <NumberOfStationaryDevices />

                        }
                        {
                            activeDashboardWidgets.includes("number_of_devices_outside_organization_boundary") &&
                            <NumberOfDevicesOutsideOrganizationBoundary />

                        }

                    </Grid>
                </Grid>
                {
                    activeDashboardWidgets.includes("locations_summary") &&
                    <Grid
                        item
                        sm={12}
                        md={6}
                        sx={{
                            width: "100%",
                            margin: isSmall ? "8px 0px 0px 0px" : '0px',
                        }} >
                        <Grid
                            container
                            spacing={isSmall ? 0 : 1}
                            sx={{
                                margin: '0px',
                                width: "100%",
                                backgroundColor: theme.palette.background.default,
                            }}
                        >

                            <LocationsSummary />
                        </Grid>
                    </Grid>

                }


                {activeDashboardWidgets.includes("number_of_devices_in_account_by_products") &&
                    numberOfDevicesByProducts({
                        devicesSummary,
                        translate, locale, theme, isSmall
                    })
                }





            </Grid >
        </>
    )
}

export default Dashboard;